import { Inbox, RotateCw, TriangleAlert, type LucideIcon } from 'lucide-react';
import type { ReactNode } from 'react';
import { ApiError, errorMessage } from '../../lib/api.ts';
import { cx } from '../../lib/cx.ts';
import { Button } from './Button.tsx';
import './States.css';

export interface EmptyStateProps {
  icon?: LucideIcon;
  title: ReactNode;
  /** At most two lines. */
  description?: ReactNode;
  /** A single call to action. */
  action?: ReactNode;
  compact?: boolean;
  className?: string;
}

export function EmptyState({ icon: Icon = Inbox, title, description, action, compact = false, className }: EmptyStateProps) {
  return (
    <div className={cx('empty-state', compact && 'empty-state--compact', className)}>
      <span className="empty-state__icon" aria-hidden="true">
        <Icon size={22} strokeWidth={1.75} />
      </span>
      <p className="empty-state__title">{title}</p>
      {description && <p className="empty-state__text">{description}</p>}
      {action && <div className="empty-state__action">{action}</div>}
    </div>
  );
}

export interface ErrorStateProps {
  title?: ReactNode;
  /** Any thrown value; ApiError shows its Thai message and reference id. */
  error?: unknown;
  message?: ReactNode;
  onRetry?: () => void;
  retrying?: boolean;
  compact?: boolean;
  className?: string;
}

/** Error inside the failing widget's own area, with Retry (DESIGN.md §14). */
export function ErrorState({ title = 'โหลดข้อมูลไม่สำเร็จ', error, message, onRetry, retrying = false, compact = false, className }: ErrorStateProps) {
  const text = message ?? (error !== undefined ? errorMessage(error) : undefined);
  const reference = error instanceof ApiError && error.correlationId ? error.correlationId.slice(0, 8) : null;
  return (
    <div role="alert" className={cx('empty-state', 'error-state', compact && 'empty-state--compact', className)}>
      <span className="empty-state__icon error-state__icon" aria-hidden="true">
        <TriangleAlert size={22} strokeWidth={1.75} />
      </span>
      <p className="empty-state__title">{title}</p>
      {text && <p className="empty-state__text">{text}</p>}
      {reference && <p className="error-state__ref">รหัสอ้างอิง {reference}</p>}
      {onRetry && (
        <div className="empty-state__action">
          <Button variant="secondary" icon={RotateCw} onClick={onRetry} loading={retrying}>
            ลองใหม่
          </Button>
        </div>
      )}
    </div>
  );
}
